import { getJson, patchJson, postFormData, postJson } from "@/lib/fetch"
import {
  captureBatchGroupsPath,
  captureBatchPath,
  captureBatchesPath,
  captureGroupPhotosPath,
  closeCaptureGroupPath,
  finishBatchCapturePath,
  resumableCaptureBatchesPath,
} from "@/routes"
import type {
  CaptureBatch,
  CaptureBatchSummary,
  CaptureGroup,
  CapturePhoto,
  QueuedCapturePhoto,
} from "@/types/capture"

const DEFAULT_MAX_ATTEMPTS = 4
const MAX_UPLOAD_ATTEMPTS = 5
const DEFAULT_BASE_DELAY_MS = 250
const MAX_RETRY_DELAY_MS = 4_000

interface DataEnvelope<T> {
  data: T
}

interface ErrorEnvelope {
  error?: {
    type?: string
    message?: string
  }
}

export interface CaptureUploadOptions {
  signal?: AbortSignal
  maxAttempts?: number
  baseDelayMs?: number
}

export class CaptureApiError extends Error {
  readonly status: number
  readonly type: string | null

  constructor(message: string, status: number, type: string | null = null) {
    super(message)
    this.name = "CaptureApiError"
    this.status = status
    this.type = type
  }
}

export async function listCaptureBatches(signal?: AbortSignal): Promise<CaptureBatchSummary[]> {
  return responseData(await getJson(captureBatchesPath(), signal))
}

export async function getResumableCaptureBatch(signal?: AbortSignal): Promise<CaptureBatch | null> {
  return responseData(await getJson(resumableCaptureBatchesPath(), signal))
}

export async function createCaptureBatch(
  containerId?: string | null,
  signal?: AbortSignal,
): Promise<CaptureBatch> {
  const body = {
    ...(containerId ? { container_id: containerId } : {}),
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
  return responseData(await postJson(captureBatchesPath(), body, signal))
}

export async function getCaptureBatch(id: string, signal?: AbortSignal): Promise<CaptureBatch> {
  return responseData(await getJson(captureBatchPath(id), signal))
}

export async function startNextCaptureGroup(
  batchId: string,
  signal?: AbortSignal,
): Promise<CaptureGroup> {
  return responseData(await postJson(captureBatchGroupsPath(batchId), undefined, signal))
}

export async function closeCaptureGroup(
  groupId: string,
  expectedPhotoCount: number,
  signal?: AbortSignal,
): Promise<CaptureGroup> {
  return responseData(
    await patchJson(
      closeCaptureGroupPath(groupId),
      {
        expected_photo_count: expectedPhotoCount,
      },
      signal,
    ),
  )
}

export async function finishCaptureBatch(
  batchId: string,
  expectedPhotoCount: number,
  signal?: AbortSignal,
): Promise<CaptureBatch> {
  return responseData(
    await patchJson(
      finishBatchCapturePath(batchId),
      {
        expected_photo_count: expectedPhotoCount,
      },
      signal,
    ),
  )
}

export async function uploadCapturePhoto(
  photo: QueuedCapturePhoto,
  options: CaptureUploadOptions = {},
): Promise<CapturePhoto> {
  const maxAttempts = boundedAttempts(options.maxAttempts)
  const baseDelayMs = boundedBaseDelay(options.baseDelayMs)

  let attempt = 0
  while (true) {
    throwIfAborted(options.signal)

    try {
      const response = await postFormData(
        captureGroupPhotosPath(photo.groupId),
        photoFormData(photo),
        options.signal,
      )
      if (response.ok) return responseData<CapturePhoto>(response)
      if (!retryableStatus(response.status) || attempt === maxAttempts - 1) {
        throw await apiError(response)
      }
    } catch (error) {
      if (isAbortError(error)) throw error
      if (!(error instanceof TypeError) || attempt === maxAttempts - 1) throw error
    }

    const delayMs = Math.min(baseDelayMs * 2 ** attempt, MAX_RETRY_DELAY_MS)
    await retryDelay(delayMs, options.signal)
    attempt += 1
  }
}

function photoFormData(photo: QueuedCapturePhoto): FormData {
  const form = new FormData()
  form.append("photo", photo.blob, photo.filename)
  form.append("client_upload_id", photo.clientUploadId)
  form.append("captured_at", new Date(photo.capturedAt).toISOString())
  return form
}

async function responseData<T>(response: Response): Promise<T> {
  if (!response.ok) throw await apiError(response)
  const body = (await response.json()) as DataEnvelope<T>
  return body.data
}

async function apiError(response: Response): Promise<CaptureApiError> {
  const body = (await response.json().catch(() => null)) as ErrorEnvelope | null
  return new CaptureApiError(
    body?.error?.message ?? `Capture request failed (${response.status})`,
    response.status,
    body?.error?.type ?? null,
  )
}

function retryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500
}

function boundedAttempts(requested?: number): number {
  if (requested === undefined || !Number.isFinite(requested)) return DEFAULT_MAX_ATTEMPTS
  return Math.min(MAX_UPLOAD_ATTEMPTS, Math.max(1, Math.floor(requested)))
}

function boundedBaseDelay(requested?: number): number {
  if (requested === undefined || !Number.isFinite(requested)) return DEFAULT_BASE_DELAY_MS
  return Math.min(MAX_RETRY_DELAY_MS, Math.max(0, requested))
}

function retryDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal)
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", abort)
      resolve()
    }, milliseconds)
    const abort = () => {
      clearTimeout(timeout)
      reject(abortReason(signal))
    }
    signal?.addEventListener("abort", abort, { once: true })
  })
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortReason(signal)
}

function abortReason(signal?: AbortSignal): Error {
  return signal?.reason instanceof Error
    ? signal.reason
    : new DOMException("The operation was aborted", "AbortError")
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError"
}
